import { ALL_SINGLE_RENDER_METHODS, OVD_EVENT_TO_SECTIONS_MAP } from "./renderSectionsMap";
import {
  BayLcgVcgTcgAndPairings,
  IBayPattern,
  IRowPattern,
  ISlotCombinedPattern,
  TGetContainersCgsResult,
  calculateTeusFromSlots,
  createRowsFromConfig,
} from "@tedivo/tedivo-bay-grid-pure";
import {
  BayLevelEnum,
  IBayLevelData,
  IOpenVesselDefinitionV1,
  IShipData,
  ISizeSummary,
  ISlotData,
} from "open-vessel-definition";
import BaysSection, { IBaysDrawOptions } from "../parts/bay-boxes/bays-section";
import {
  DivWithSpinner,
  IDictionaryDisplayElement,
  createSimpleAlert,
  mapDataToDictionary,
} from "@tedivo/tedivo-ui";
import { ICGsMissingOutput, TOutOfBoundsCgsResult } from "@tedivo/tedivo-ovd-validators";
import ViewCgsConfig, { ICgsPartData } from "../config/ViewCgsConfig";
import { getTranslation, i18n } from "../../../../app/i18/i18tn";
import ovdJsonStore, { ICustomOVDChangeEvent } from "../../../../app/stores/OVDJsonStore";

import CellSpacingEnum from "../../../../app/enums/CellSpacingEnum";
import GroupBaysPairedEnum from "../../../../app/enums/GroupBaysPairedEnum";
import { IFilesTags } from "@tedivo/tvd-api-models";
import type RendererForShip from "../parts/edits/helpers/viewEdit3D/draw/render/RendererForShip";
import { SelectShoelace } from "@tedivo/tedivo-form";
import SizeSmallMidLargeEnum from "../../../../app/enums/SizeSmallMidLargeEnum";
import { SlSelect } from "@shoelace-style/shoelace";
import SlTooltip from "@shoelace-style/shoelace/dist/components/tooltip/tooltip";
import { ViewJsonTypeEnum } from "../ViewJsonTypeEnum";
import ViewShipDataConfig from "../config/ViewShipDataConfig";
import ViewSizeSummaryConfig from "../config/ViewSizeSummaryConfig";
import { createDeckView } from "./createDeckView";
import { createPart } from "./createPart";
import { createSideView } from "./createSideView";
import { getPreferencesValue } from "@tedivo/tedivo-preferences";
import getTranslatedDictionary from "../../../../app/i18/getTranslatedDictionary";
import { pad3 } from "@tedivo/tedivo-pure-helpers";
import { removeChildren } from "@tedivo/tedivo-dom-helpers";

export default class ViewJsonRender {
  private nodes: ViewJsonParts;

  private mainNode: HTMLDivElement;

  private props: ViewJsonRenderProps;
  private context: {
    totalTEUs?: number;
    containersWithPositions?: TGetContainersCgsResult;
    buttonsAndActions?: IEditButtonsAndActions;
  } = {};

  private rendererForShip: RendererForShip | undefined = undefined;
  private baysSection: BaysSection | undefined;
  private bayDrawOptions: IBaysDrawOptions;

  get node() {
    return this.mainNode;
  }

  set viewD3ClearColor(color: "dark" | "light") {
    if (!this.rendererForShip) return;
    this.rendererForShip.colorMode = color;
  }

  get bayGroupedDisplay() {
    return this.bayDrawOptions.baysPairedGrouped;
  }
  set bayGroupedDisplay(value: GroupBaysPairedEnum) {
    if (this.bayDrawOptions.baysPairedGrouped !== value)
      this.bayDrawOptions.baysPairedGrouped = value;

    this.nodes.baySection.bayBoxes.setAttribute(
      "data-group-paired-bays",
      String(value === GroupBaysPairedEnum.GROUPED),
    );
  }

  get fit2Vertical() {
    return this.bayDrawOptions.fit2Vertical;
  }
  set fit2Vertical(value: boolean) {
    if (this.bayDrawOptions.fit2Vertical !== value) this.bayDrawOptions.fit2Vertical = value;

    this.baysSection?.calcAndSetScale(value);
  }

  constructor(props: ViewJsonRenderProps, containersWithPositions: TGetContainersCgsResult) {
    this.props = props;
    this.context.containersWithPositions = containersWithPositions;
    this.bayDrawOptions = getBayDrawPreferences();

    this.mainNode = document.createElement("div");
    this.nodes = this.buildHtml(this.mainNode);
    this.bayGroupedDisplay = this.bayDrawOptions.baysPairedGrouped;
    this.fit2Vertical = this.bayDrawOptions.fit2Vertical;

    this.render(props.json, undefined, props.lcgVcgTcgAndPairings);
  }

  public updateData({
    event,
    json,
    lcgVcgTcgAndPairings,
    containersWithPositions,
  }: {
    event?: CustomEvent<ICustomOVDChangeEvent>;
    json: IOpenVesselDefinitionV1;
    lcgVcgTcgAndPairings?: BayLcgVcgTcgAndPairings;
    containersWithPositions: TGetContainersCgsResult;
  }) {
    if (!json || !lcgVcgTcgAndPairings) {
      console.warn("No data to render");
      return;
    }

    // 1. Update data
    this.props.json = json;
    this.props.lcgVcgTcgAndPairings = lcgVcgTcgAndPairings;
    this.context.containersWithPositions = containersWithPositions;

    this.render(json, event, lcgVcgTcgAndPairings);
  }

  /** Updates the size and spacing options and redraws all the bays */
  public updateDrawPreferences(
    size: SizeSmallMidLargeEnum | undefined,
    cellSpacing: CellSpacingEnum | undefined,
    newBayGrouped: GroupBaysPairedEnum | undefined,
    fit2Vertical: boolean | undefined,
  ) {
    if (size !== undefined) this.bayDrawOptions.cellSize = size;

    if (cellSpacing !== undefined) this.bayDrawOptions.cellSpacing = cellSpacing;

    if (newBayGrouped !== undefined) this.bayGroupedDisplay = newBayGrouped;

    if (fit2Vertical !== undefined) this.fit2Vertical = fit2Vertical;

    this.renderBays();
  }

  private renderGeneral = () => {
    const json = this.props.json;
    if (!json) return;

    this.nodes.general.withLoading<void>(async () => {
      const shipNameAkas = (ovdJsonStore.tvdId?.shipNameAkaStr?.split("|") || []).filter(
        (s) => s && s !== json.shipData.shipName,
      );
      const viewShipData = {
        ...json.shipData,
        shipNameAkas: shipNameAkas.length ? shipNameAkas : undefined,
      };

      const shipPartDataElements = getTranslatedDictionary(
        mapDataToDictionary<IShipData>(viewShipData, ViewShipDataConfig, i18n.language),
      );

      const partConfig: IPartConfig = {
        name: "shipData",
        title: "view:shipDataTitle",
        elements: shipPartDataElements,
        editAction: this.context.buttonsAndActions?.editGeneralButton,
      };

      const htmlOutput = createPart(
        partConfig.name,
        partConfig.title,
        partConfig.elements,
        partConfig.editAction,
      );

      removeChildren(this.nodes.general);
      this.nodes.general.dataset.lastUpdated = new Date().toISOString();
      this.nodes.general.appendChild(htmlOutput);
    });
  };

  private renderSizeSummary = () => {
    const json = this.props.json;
    if (!json) return;

    this.nodes.sizeSummary.withLoading<void>(async () => {
      const sizeSummaryDataElements = getTranslatedDictionary(
        mapDataToDictionary<ISizeSummary & { totalTEUs: number }>(
          { ...json.sizeSummary, totalTEUs: this.context.totalTEUs || 0 },
          ViewSizeSummaryConfig,
          i18n.language,
        ),
      );

      const partConfig: IPartConfig = {
        name: "sizeSummary",
        title: "view:sizeSummaryTitle",
        elements: sizeSummaryDataElements,
        editAction: this.context.buttonsAndActions?.editSizeSummaryButton,
      };

      const htmlOutput = createPart(
        partConfig.name,
        partConfig.title,
        partConfig.elements,
        partConfig.editAction,
      );

      removeChildren(this.nodes.sizeSummary);
      this.nodes.sizeSummary.dataset.lastUpdated = new Date().toISOString();
      this.nodes.sizeSummary.appendChild(htmlOutput);
    });
  };

  private renderCenterOfGravity = () => {
    const json = this.props.json;
    const cgsStats = this.props.lcgVcgTcgAndPairings?.cgStats;

    if (!json || !cgsStats) return;

    this.nodes.centersOfGravity.withLoading<void>(async () => {
      const cgsPartData: ICgsPartData = {
        lcgValues: json.shipData.lcgOptions.values,
        vcgValues: json.shipData.vcgOptions.values,
        tcgValues: json.shipData.tcgOptions.values,
        lcgDataFilled: Math.floor((cgsStats.definedLcgs / (cgsStats.countLcgs || 1)) * 100),
        vcgDataFilled: Math.floor(
          (cgsStats.definedBottomBase / (cgsStats.countBottomBase || 1)) * 100,
        ),
        tcgDataFilled: Math.floor((cgsStats.definedTcgs / (cgsStats.countTcgs || 1)) * 100),
        lpp: json.shipData.lcgOptions.lpp,
        loa: json.shipData.loa || 0,
        sternToAftPp: json.shipData.sternToAftPp || 0,
      };

      const cgsPartDataElements = getTranslatedDictionary(
        mapDataToDictionary<ICgsPartData>(cgsPartData, ViewCgsConfig, i18n.language),
      );

      {
        // Anomalies in CGs? Show button and dialog
        const cgsValidatorResult =
          ovdJsonStore.ovdValidator.getLastResultByName<ICGsMissingOutput>("cgsValidator");
        const cgsSumry = cgsValidatorResult?.result.summary.outOfBounds;
        const anomaliesButton = showCGsAnomaliesButton(cgsSumry);

        if (anomaliesButton) {
          cgsPartDataElements.push({
            label: getTranslation("view:cgs.validationWarning"),
            value: anomaliesButton,
            type: "HtmlElement",
          });
        }
      }

      const partConfig: IPartConfig = {
        name: "cgsSummary",
        title: "view:cgsDataTitle",
        elements: cgsPartDataElements,
        disabled: !this.props.canOpenDrawer,
        editAction: this.context.buttonsAndActions?.editCGsButton,
      };

      const htmlOutput = createPart(
        partConfig.name,
        partConfig.title,
        partConfig.elements,
        partConfig.editAction,
      );

      removeChildren(this.nodes.centersOfGravity);
      this.nodes.centersOfGravity.dataset.lastUpdated = new Date().toISOString();
      this.nodes.centersOfGravity.appendChild(htmlOutput);
    });
  };

  private renderSideView() {
    const json = this.props.json;
    const lcgVcgTcgAndPairings = this.props.lcgVcgTcgAndPairings;
    const containersWithPositions = this.context.containersWithPositions;
    const sideViewNode = this.nodes.sideView;

    if (!json || !lcgVcgTcgAndPairings || !containersWithPositions) return;

    const tooltip = document.createElement("sl-tooltip");
    tooltip.content = getTranslation("view:sideView.displayConts.tooltip");
    tooltip.placement = "left-start";

    const selectContainers = document.createElement("sl-select");
    selectContainers.size = "small";

    const options = [
      {
        value: "maxSize",
        text: getTranslation("view:sideView.displayConts.maxSize"),
      },
      {
        value: "stbdOuter",
        text: getTranslation("view:sideView.displayConts.stbdOuter"),
      },
      {
        value: "portOuter",
        text: getTranslation("view:sideView.displayConts.portOuter"),
      },
      { value: "all", text: getTranslation("view:sideView.displayConts.all") },
      ...createRowsFromConfig(!!json.sizeSummary.centerLineRow, json.sizeSummary.maxRow).map(
        (row) => ({
          value: row,
          text: getTranslation("view:rowNumber", { row }),
        }),
      ),
    ];

    options.forEach((option) => {
      const selectOption = document.createElement("sl-option");
      selectOption.value = option.value;
      selectOption.innerHTML = option.text;
      selectContainers.appendChild(selectOption);
    });

    selectContainers.value = "maxSize";
    selectContainers.addEventListener("sl-change", () => {
      renderSideViewInternal(
        json,
        lcgVcgTcgAndPairings,
        containersWithPositions,
        sideViewNode,
        actionsDiv,
      );
    });

    tooltip.appendChild(selectContainers);

    const actionsDiv = document.createElement("div");
    actionsDiv.style.display = "flex";
    actionsDiv.style.gap = "0.5em";
    actionsDiv.appendChild(tooltip);

    if (this.context.buttonsAndActions?.editSideViewActions)
      actionsDiv.appendChild(this.context.buttonsAndActions.editSideViewActions);

    renderSideViewInternal(
      json,
      lcgVcgTcgAndPairings,
      containersWithPositions,
      sideViewNode,
      actionsDiv,
    );

    function renderSideViewInternal(
      json: IOpenVesselDefinitionV1,
      lcgVcgTcgAndPairings: BayLcgVcgTcgAndPairings,
      containersWithPositions: TGetContainersCgsResult,
      sideViewNode: DivWithSpinner,
      actionsDiv: HTMLElement,
    ) {
      sideViewNode.withLoading<void>(async () => {
        const htmlOutput = await createSideView({
          sizeSummary: json.sizeSummary,
          baysData: json.baysData,
          shipData: json.shipData,
          vesselPartsData: json.vesselPartsData || [],
          v3DParams: ovdJsonStore.tvdId?.v3DParams,
          offsetBottomBases: ovdJsonStore.currentJson?.shipData.masterCGs?.offsetBottomBases,
          lcgVcgTcgAndPairings,
          containersWithPositions,
          editActionNode: actionsDiv,
          selectContainerCriteria: selectContainers.value as
            | "stbdOuter"
            | "portOuter"
            | "all"
            | IRowPattern,
          bayNumbersDoubleClickAction: doubleClickOnSvgBay,
        });

        if (selectContainers.value === "portOuter") {
          const svg = htmlOutput.querySelector("svg");
          if (svg) {
            svg.style.transform = "scaleX(-1)";
          }
          const topNumbers = htmlOutput.querySelectorAll(
            ".top-bay-number",
          ) as NodeListOf<SVGTextElement>;
          topNumbers.forEach((el) => {
            if (el) {
              el.style.transform = "scaleX(-1)";
              el.setAttribute("x", String(-Number(el.getAttribute("x"))));
            }
          });
        }

        removeChildren(sideViewNode);
        sideViewNode.dataset.lastUpdated = new Date().toISOString();
        sideViewNode.appendChild(htmlOutput);
      });
    }
  }

  private renderDeckView() {
    const json = this.props.json;
    const lcgVcgTcgAndPairings = this.props.lcgVcgTcgAndPairings;
    const containersWithPositions = this.context.containersWithPositions;
    const deckViewNode = this.nodes.deckView;

    if (!json || !lcgVcgTcgAndPairings || !containersWithPositions) return;

    // const tooltip = document.createElement("sl-tooltip");
    // tooltip.content = getTranslation("view:sideView.displayConts.tooltip");
    // tooltip.placement = "left-start";

    // const selectContainers = document.createElement("sl-select");
    // selectContainers.size = "small";

    // const options = [
    //   {
    //     value: "highestBelowOrLowestAbove",
    //     text: getTranslation(
    //       "view:deckView.displayConts.highestBelowOrLowestAbove",
    //     ),
    //   },
    //   {
    //     value: "highestAll",
    //     text: getTranslation("view:deckView.displayConts.highestAll"),
    //   },
    //   {
    //     value: "highestBelow",
    //     text: getTranslation("view:deckView.displayConts.highestBelow"),
    //   },
    // ];

    // options.forEach((option) => {
    //   const selectOption = document.createElement("sl-option");
    //   selectOption.value = option.value;
    //   selectOption.innerHTML = option.text;
    //   selectContainers.appendChild(selectOption);
    // });

    // selectContainers.value = "highestBelowOrLowestAbove";
    // selectContainers.style.width = "260px";
    // selectContainers.addEventListener("sl-change", () => {
    //   renderDeckViewInternal(
    //     json,
    //     lcgVcgTcgAndPairings,
    //     containersWithPositions,
    //     deckViewNode,
    //     actionsDiv,
    //   );
    // });

    // tooltip.appendChild(selectContainers);

    const actionsDiv = document.createElement("div");
    actionsDiv.style.display = "flex";
    actionsDiv.style.gap = "0.5em";
    // actionsDiv.appendChild(tooltip);

    if (this.context.buttonsAndActions?.editDeckViewActions) {
      actionsDiv.appendChild(this.context.buttonsAndActions.editDeckViewActions);
    }

    renderDeckViewInternal(
      json,
      lcgVcgTcgAndPairings,
      containersWithPositions,
      deckViewNode,
      actionsDiv,
    );

    async function renderDeckViewInternal(
      json: IOpenVesselDefinitionV1,
      lcgVcgTcgAndPairings: BayLcgVcgTcgAndPairings,
      containersWithPositions: TGetContainersCgsResult,
      deckViewNode: DivWithSpinner,
      actionsDiv: HTMLElement,
    ) {
      const htmlOutput = await createDeckView({
        sizeSummary: json.sizeSummary,
        masterCGs: json.shipData.masterCGs,
        baysData: json.baysData,
        lidData: json.lidData,
        vesselPartsData: json.vesselPartsData || [],
        v3DParams: ovdJsonStore.tvdId?.v3DParams,
        lcgVcgTcgAndPairings,
        containersWithPositions,
        editActionNode: actionsDiv,
        selectContainerCriteria: "highestBelow", //  selectContainers.value as IDeckSelectContainerCriteria,
        bayNumbersDoubleClickAction: doubleClickOnSvgBay,
      });

      removeChildren(deckViewNode);
      deckViewNode.dataset.lastUpdated = new Date().toISOString();
      deckViewNode.appendChild(htmlOutput);
    }
  }

  private renderD3View = async () => {
    const json = this.props.json;
    const lcgVcgTcgAndPairings = this.props.lcgVcgTcgAndPairings;
    const containersWithPositions = this.context.containersWithPositions;

    if (
      !json ||
      !lcgVcgTcgAndPairings ||
      lcgVcgTcgAndPairings.cgStats.totalSlotsCount < 10 ||
      !containersWithPositions
    )
      return;

    if (!this.rendererForShip) {
      import(/* webpackChunkName: "create3DView", webpackPrefetch: true */ "./create3DView").then(
        (module) => {
          const { container, renderer } = module.create3DView({
            shipData: json.shipData,
            sizeSummary: json.sizeSummary,
            baysData: json.baysData,
            vesselPartsData: json.vesselPartsData || [],
            lcgVcgTcgAndPairings,
            offsetBottomBases: ovdJsonStore.currentJson?.shipData.masterCGs?.offsetBottomBases,
            v3DParams: ovdJsonStore.tvdId?.v3DParams,
            colorMode: this.props.colorMode,
            containersWithPositions,
            onLoadedCb,
          });

          this.rendererForShip = renderer;

          const { card, chkMouseWheelZoom } = create3DCardNodes(
            this.rendererForShip,
            undefined, //this.context.buttonsAndActions?.editView3DActions,
            this.nodes.d3View.baysSelector,
          );

          card.appendChild(container);

          chkMouseWheelZoom.checked = false;
          renderer.mouseWheelZoom = false;

          removeChildren(this.nodes.d3View.holder);
          this.nodes.d3View.holder.dataset.lastUpdated = new Date().toISOString();
          this.nodes.d3View.holder.appendChild(card);

          renderer.onWindowResize();
          renderer.shouldAnimate = 3000;
          renderer.animate();

          setTimeout(() => {
            renderer.resetView3DInShip();
          }, 3000);

          function onLoadedCb() {
            card.isLoading = false;
          }
        },
      );
    } else {
      this.rendererForShip.reInit({
        shipData: json.shipData,
        sizeSummary: json.sizeSummary,
        baysData: json.baysData,
        vesselPartsData: json.vesselPartsData || [],
        lcgVcgTcgAndPairings,
        offsetBottomBases: ovdJsonStore.currentJson?.shipData.masterCGs?.offsetBottomBases,
        v3DParams: ovdJsonStore.tvdId?.v3DParams,
        containersWithPositions,
      });

      createBaysSelectListOptions(this.nodes.d3View.baysSelector);
    }

    function createBaysSelectListOptions(selectContainers: SlSelect) {
      const prevValue = selectContainers.value;

      const options = [
        {
          value: "all",
          text: getTranslation("view:view3D.view3DActions.allBays"),
        },
        ...(
          lcgVcgTcgAndPairings?.blockBaysAndSizes
            .filter((bs) => bs.definedSlots > 0)
            .map((bs) => bs.allBays) || []
        ).map((allBays) => {
          const baysN = allBays.split("-").map(Number);
          const allOk = baysN.every((n) => !isNaN(n));
          const label =
            baysN.length === 1 || !allOk
              ? allBays
              : `${allBays} (${pad3((baysN[0] + baysN[1]) * 0.5)})`;

          return {
            value: allBays,
            text: getTranslation("view:view3D.view3DActions.bay", {
              bay: label,
            }),
          };
        }),
      ];

      removeChildren(selectContainers);

      options.forEach((option) => {
        const selectOption = document.createElement("sl-option");
        selectOption.value = option.value;
        selectOption.innerHTML = option.text;
        selectContainers.appendChild(selectOption);
      });

      if (prevValue) {
        selectContainers.value = prevValue;
      }
    }

    function create3DCardNodes(
      rendererForShip: RendererForShip | undefined,
      editView3DActions: HTMLElement | undefined,
      selectContainers: SlSelect,
    ) {
      // Action Box
      const view3DActionBox = document.createElement("div");
      view3DActionBox.style.display = "flex";
      view3DActionBox.style.gap = "1em";
      view3DActionBox.style.alignItems = "baseline";

      // Reset View Button
      const reset3DViewButton = document.createElement("sl-button");
      reset3DViewButton.size = "small";
      reset3DViewButton.pill = true;
      reset3DViewButton.innerHTML = getTranslation("view:view3D.view3DActions.reset");
      reset3DViewButton.addEventListener("click", () => {
        rendererForShip?.resetView3DInShip();
      });

      const tooltipBaySelector = document.createElement("sl-tooltip");
      tooltipBaySelector.content = getTranslation("view:view3D.view3DActions.baySelector");
      tooltipBaySelector.placement = "left-start";

      createBaysSelectListOptions(selectContainers);
      selectContainers.size = "small";
      selectContainers.value = "all";
      selectContainers.addEventListener("sl-change", () => {
        rendererForShip?.setContainerVisibility(selectContainers.value as string);
      });

      tooltipBaySelector.appendChild(selectContainers);

      // Activate toggle Button
      const chkMouseWheelZoom = document.createElement("sl-switch");
      chkMouseWheelZoom.size = "small";
      chkMouseWheelZoom.innerHTML = getTranslation("view:view3D.view3DActions.mouseWheelZoom");
      chkMouseWheelZoom.className = "btn-activate-3d";
      chkMouseWheelZoom.setAttribute("part", "part-activate-3d");
      chkMouseWheelZoom.addEventListener("sl-change", () => {
        if (!rendererForShip) return;
        rendererForShip.mouseWheelZoom = chkMouseWheelZoom.checked;
      });

      view3DActionBox.appendChild(tooltipBaySelector);
      view3DActionBox.appendChild(chkMouseWheelZoom);
      view3DActionBox.appendChild(reset3DViewButton);

      // Edit Button
      if (editView3DActions) view3DActionBox.appendChild(editView3DActions);

      // Card of 3D View
      const card = document.createElement("card-element");
      card.titleHtml = "view:3dView.title";
      card.className = "card-3d-view";
      card.name = "3dView";
      card.isLoading = true;
      card.actions = view3DActionBox;

      return {
        card,
        chkMouseWheelZoom,
      };
    }
  };

  private renderBays(eventDetail?: ICustomOVDChangeEvent) {
    const bays = eventDetail?.data?.map((bay) => bay.split("-")[0]) as IBayPattern[] | undefined;

    const btnsAndActions = this.context.buttonsAndActions;
    if (!btnsAndActions) return;

    if (!this.baysSection) {
      this.baysSection = new BaysSection({
        createEditBayButtonFunction: btnsAndActions.createEditBayButtonFunction,
        readonlyMode: this.props.readonlyMode,
        canOpenDrawer: this.props.canOpenDrawer,
        bayHelpersButton: btnsAndActions.bayHelpersButton,
        shipName: this.props.json?.shipData.shipName || "",
      });

      this.baysSection?.calcAndSetScale(this.bayDrawOptions.fit2Vertical);

      this.nodes.baySection.bayBoxes.appendChild(this.baysSection.mainNode);
    }

    this.baysSection.render({
      lcgVcgTcgAndPairings: this.props.lcgVcgTcgAndPairings,
      bayDrawOptions: this.bayDrawOptions,
      baysToRender: bays,
    });
  }

  private buildHtml(mainNode: HTMLDivElement): ViewJsonParts {
    const nodes: ViewJsonParts = {
      general: document.createElement("div-spinner-element"),
      sizeSummary: document.createElement("div-spinner-element"),
      centersOfGravity: document.createElement("div-spinner-element"),
      sideView: document.createElement("div-spinner-element"),
      deckView: document.createElement("div-spinner-element"),
      d3View: {
        holder: document.createElement("div-spinner-element"),
        baysSelector: document.createElement("sl-select"),
      },
      baySection: {
        holder: document.createElement("div-spinner-element"),
        legend: document.createElement("div"),
        bayBoxes: document.createElement("div"),
      },
    };

    nodes.baySection.holder.appendChild(nodes.baySection.legend);
    nodes.baySection.holder.appendChild(nodes.baySection.bayBoxes);

    mainNode.appendChild(nodes.general);
    mainNode.appendChild(nodes.sizeSummary);
    mainNode.appendChild(nodes.centersOfGravity);
    mainNode.appendChild(nodes.sideView);
    mainNode.appendChild(nodes.d3View.holder);
    mainNode.appendChild(nodes.deckView);
    mainNode.appendChild(nodes.baySection.holder);

    return nodes;
  }

  render = (
    json: IOpenVesselDefinitionV1 | undefined,
    event: CustomEvent<ICustomOVDChangeEvent> | undefined,
    lcgVcgTcgAndPairings: BayLcgVcgTcgAndPairings | undefined,
  ) => {
    if (!json || !lcgVcgTcgAndPairings) {
      console.warn("No data to render");
      return;
    }
    // 1. Update context
    this.context.totalTEUs = calculateTeusFromSlots(json.baysData);

    this.context.buttonsAndActions = this.props.createEditButtonsAndActions(lcgVcgTcgAndPairings);

    // 2. Select render methods
    const type = event?.detail?.type;
    const renderMethods = type
      ? OVD_EVENT_TO_SECTIONS_MAP[type] || ALL_SINGLE_RENDER_METHODS
      : ALL_SINGLE_RENDER_METHODS;

    // 3. Render
    renderMethods.forEach((method) => this[method](event?.detail));
  };
}

function doubleClickOnSvgBay(node: SVGElement | HTMLElement) {
  const bay = node.dataset.bay;
  const el = document.getElementById(`bay-box-component-${bay}`);
  if (el) el.scrollIntoView({ block: "center", behavior: "auto" });
}

function getBayDrawPreferences(): IBaysDrawOptions {
  const baySizeOption = getPreferencesValue("bay-data-size") as SizeSmallMidLargeEnum;

  const bayCellSeparationOption = getPreferencesValue("bay-cell-separation") as CellSpacingEnum;

  const bayGroupedPairedOption = getPreferencesValue("bay-grouped-paired") as GroupBaysPairedEnum;

  const fit2Vertical = getPreferencesValue("bay-fit2-vertical") as "true" | "false" | boolean;

  return {
    cellSize: baySizeOption ?? SizeSmallMidLargeEnum.SMALL,
    cellSpacing: bayCellSeparationOption ?? CellSpacingEnum.NO_SPACE,
    baysPairedGrouped: bayGroupedPairedOption ?? GroupBaysPairedEnum.NOT_GROUPED,
    fit2Vertical: fit2Vertical === true || fit2Vertical === "true",
  };
}

function showCGsAnomaliesButton(
  cgsSumry: TOutOfBoundsCgsResult | undefined,
): HTMLElement | undefined {
  if (!cgsSumry) return undefined;

  if (
    !cgsSumry.lcgsMightBeOutOfRange &&
    !cgsSumry.loaIsLowerThanLpp &&
    !cgsSumry.loaMighBeOutOfRange &&
    !cgsSumry.lppMighBeOutOfRange &&
    cgsSumry.baysOutOfBounds.length === 0
  )
    return undefined;

  const anomaliesButton = document.createElement("sl-button");
  anomaliesButton.variant = "danger";
  anomaliesButton.innerHTML = getTranslation("view:cgs.seeAnomalies");
  anomaliesButton.size = "small";
  const dangerIcon = document.createElement("sl-icon");
  dangerIcon.name = "exclamation-diamond";
  dangerIcon.slot = "prefix";
  anomaliesButton.appendChild(dangerIcon);
  anomaliesButton.addEventListener("click", showCgsAnomaliesDetailsDialog);

  return anomaliesButton;

  function showCgsAnomaliesDetailsDialog() {
    if (!cgsSumry) return;

    const booleanProps: Array<keyof TOutOfBoundsCgsResult> = [
      "loaIsLowerThanLpp",
      "loaMighBeOutOfRange",
      "lppMighBeOutOfRange",
      "lcgsMightBeOutOfRange",
    ];

    const messages: string[] = [];
    booleanProps.forEach((prop) => {
      if (cgsSumry[prop]) messages.push(`view:cgs.${prop}`);
    });

    const div = document.createElement("div");
    if (messages.length) {
      messages.forEach((m) => {
        const mNode = document.createElement("div");
        mNode.innerHTML = getTranslation(m) + ".";
        div.appendChild(mNode);
      });
    }

    if (cgsSumry.baysOutOfBounds.length) {
      const mNode = document.createElement("div");
      mNode.innerHTML = getTranslation("view:cgs.baysOutOfBounds") + ":";
      div.appendChild(mNode);

      const ul = document.createElement("ul");
      cgsSumry.baysOutOfBounds.forEach((i) => {
        const li = document.createElement("li");
        li.innerHTML = `${i.bay} 
          ${getTranslation(`enums:BayLevelEnum.${BayLevelEnum[i.level]}`)}
          ${i.size}ft: ${i.reasons
          .map((r) => getTranslation(`view:cgs.ReasonCgsWrong.${r}`))
          .join(", ")}
        `;
        ul.appendChild(li);
      });
      div.appendChild(ul);
    }

    createSimpleAlert({
      title: getTranslation("view:cgs.validationWarning"),
      message: div,
      closeLabel: getTranslation("general:common.ok"),
      width: "700px",
    });
  }
}

type ViewJsonRenderProps = {
  json: IOpenVesselDefinitionV1 | undefined;
  fileId: string;
  fileOrgId: string;
  currentOrgId: string;
  canOpenDrawer: boolean;
  readonlyMode: boolean;
  viewType: ViewJsonTypeEnum;
  version?: string;
  fileTags: IFilesTags;
  lcgVcgTcgAndPairings?: BayLcgVcgTcgAndPairings;
  colorMode: "dark" | "light";
  createEditButtonsAndActions: (
    lcgVcgTcgAndPairings: BayLcgVcgTcgAndPairings,
  ) => IEditButtonsAndActions;
};

interface ViewJsonParts {
  general: DivWithSpinner;
  sizeSummary: DivWithSpinner;
  centersOfGravity: DivWithSpinner;
  sideView: DivWithSpinner;
  deckView: DivWithSpinner;
  d3View: {
    holder: DivWithSpinner;
    baysSelector: SlSelect;
  };
  baySection: {
    holder: DivWithSpinner;
    legend: HTMLElement;
    bayBoxes: HTMLElement;
  };
}

type IPartConfig = {
  name: string;
  title: string;
  elements: IDictionaryDisplayElement[];
  editAction?: HTMLElement;
  disabled?: boolean;
};

export interface IEditButtonsAndActions {
  editCGsButton: SlTooltip | SelectShoelace<string>;
  editSideViewActions: SlTooltip | SelectShoelace<string>;
  editGeneralButton: SlTooltip | SelectShoelace<string> | HTMLElement;
  editSizeSummaryButton: SlTooltip | SelectShoelace<string> | HTMLElement;
  editDeckViewActions: SlTooltip | SelectShoelace<string> | HTMLElement;
  editView3DActions: SlTooltip | SelectShoelace<string> | HTMLElement;
  bayHelpersButton: SlTooltip | SelectShoelace<string> | HTMLElement;

  createEditBayButtonFunction: TEditBayButtonFunction;
}

export type TEditBayButtonFunction = (
  isoBay: IBayPattern,
  sizeSummary: ISizeSummary,
  shipData: IShipData,
  bayAbove: IBayLevelData | undefined,
  bayBelow: IBayLevelData | undefined,
  enabledCells: ISlotCombinedPattern[],
  slotsDataAbove: ISlotData[],
  slotsDataBelow: ISlotData[],
) => SlTooltip | SelectShoelace<string>;
